<script setup lang="ts">
/**
 * Mini carte Leaflet + OpenStreetMap (page association, événement, adhésion).
 *
 * Lot 4 (2026-09-27) — la carte trouve le bon endroit PARTOUT :
 *  - point ENREGISTRÉ d'abord (`latitude` / `longitude` / `precision`,
 *    posés par l'API à la saisie) : aucun géocodage, affichage immédiat.
 *    Depuis le §18 du contrat (28/09/2026), ce point est TOUJOURS libre :
 *    BAN en France, OpenStreetMap à l'étranger — l'API n'écrit ni n'expose
 *    jamais un point Google (interdit sur une carte non Google comme
 *    celle-ci). Zoom : règle partagée `zoomCarteDepuisPrecision` (= mobile) ;
 *  - repli (ancien lieu sans point) : Géoplateforme (BAN), seulement quand
 *    la BAN couvre l'adresse — même règle que la saisie, `fournisseurAdresse`,
 *    avec le PAYS et le TERRITOIRE connus du lieu ou de l'association
 *    (« …, 1000 Bruxelles » d'une association belge n'est pas cherché en
 *    France ; Papeete, Nouméa, Mata-Utu non plus : la BAN ne les couvre pas) ;
 *    le résultat est choisi par la règle d'acceptation PARTAGÉE avec l'API et
 *    la tâche geo:localiser du backup-manager (`resultatBanRetenu`, contrat
 *    §17.6.2) : score ≥ 0,6, même code postal, ou ville nommée dans le
 *    territoire attendu, et AUCUN résultat quand plusieurs communes homonymes
 *    restent (« Saint-Pierre » : 972, 974, 975). La carte publique ne pose
 *    donc jamais un point que le serveur aurait refusé ; sinon, marqueur
 *    neutre plutôt qu'un faux endroit ;
 *  - plus AUCUN appel à Nominatim depuis le navigateur des visiteurs
 *    (conditions d'usage d'OpenStreetMap, et résultats hors sujet).
 */
import "leaflet/dist/leaflet.css";
import {
  adresseDepuisFeatureBan,
  analyserAdresseLibre,
  BAN_URL_RECHERCHE,
  fournisseurAdresse,
  lienItineraire,
  resultatBanRetenu,
  TERRITOIRES_OUTRE_MER,
  territoireConnu,
  texteRechercheBan,
  type FeatureBan,
  type PrecisionAdresse,
  type TerritoireConnu,
  zoomCarteDepuisPrecision,
} from "@donoor/utils";
import { logger } from "~/utils/logger";

const props = defineProps<{
  address: string;
  height?: string;
  /** Point enregistré (facultatif : absent des anciennes données). */
  latitude?: number | null;
  longitude?: number | null;
  precision?: PrecisionAdresse | string | null;
  /**
   * Pays ISO de l'adresse (morceaux enregistrés, sinon pays de
   * l'association) et territoire d'outre-mer : décident du repli (la BAN ne
   * couvre que la France hors 986-988). Absents → lus dans le texte, France
   * à défaut (comportement d'avant).
   */
  pays?: string | null;
  territoire?: string | null;
  /**
   * Département connu (« 972 », « 75 ») des mêmes morceaux : avec
   * `territoire`, donne le territoire ATTENDU d'un texte sans code postal
   * (`territoireConnu` : département seul = métropole ; rien = inconnu).
   */
  departement?: string | null;
  /** Ville saisie d'un lieu (`location_city`) : un résultat de cette ville est accepté (= API). */
  villeLieu?: string | null;
}>();

/** Résultats demandés à la Géoplateforme : plusieurs, pour voir les homonymes (= API, backup-manager). */
const BAN_RESULTATS = 5;

const mapContainer = ref<HTMLDivElement | null>(null);
const mapReady = ref(false);
const mapError = ref(false);
// Reason: leaflet's Map type isn't easily importable at top-level due to dynamic import
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let mapInstance: any = null;

const estNombre = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/** Point enregistré, s'il est fourni et valide. */
const pointEnregistre = computed(() =>
  estNombre(props.latitude) && estNombre(props.longitude)
    ? { lat: props.latitude, lng: props.longitude }
    : null,
);

const coords = ref<{ lat: number; lng: number } | null>(pointEnregistre.value);
const precisionPoint = ref<string | null>(pointEnregistre.value ? (props.precision ?? null) : null);

/**
 * Territoire ATTENDU d'un texte (= API et backup-manager, §17.6.2) : un code
 * postal ou un territoire NOMMÉ dans le texte désigne le sien (null =
 * métropole) ; sinon le territoire CONNU des morceaux (lieu ou association) ;
 * sinon inconnu (pas de contrôle de territoire, les homonymes restent
 * refusés).
 */
function territoireAttendu(analyse: { codePostal: string | null; territoire: string | null }): TerritoireConnu | null {
  if (analyse.codePostal || analyse.territoire) return { territoire: analyse.territoire };
  return territoireConnu({
    address_territory: props.territoire?.trim() || null,
    address_department: props.departement?.trim() || null,
  });
}

/** Repli : géocodage BAN (Géoplateforme) d'une adresse sans point, si la BAN la couvre. */
async function geocode() {
  const texte = props.address?.trim();
  if (!texte) return;
  const pays = props.pays?.trim() || null;
  const territoire = props.territoire?.trim() || null;
  if (fournisseurAdresse({ pays, territoire, texte }) !== "ban") {
    mapError.value = true;
    return;
  }
  try {
    const analyse = analyserAdresseLibre(texte, { paysParDefaut: pays, territoireParDefaut: territoire });
    const attendu = territoireAttendu(analyse);
    // Texte d'un GÉOCODAGE BAN : règle partagée `texteRechercheBan` (≤ 200
    // caractères, la Géoplateforme refuse au-delà) — un texte long garde sa
    // rue et « CP Ville » au lieu d'être coupé au 200e caractère.
    const params = new URLSearchParams({ q: texteRechercheBan(texte), limit: String(BAN_RESULTATS) });
    // Biais vers le territoire attendu (= API) : départage les homonymes.
    const centre = TERRITOIRES_OUTRE_MER.find((t) => t.code === attendu?.territoire)?.centre;
    if (centre) {
      params.set("lat", String(centre.latitude));
      params.set("lon", String(centre.longitude));
    }
    const res = await fetch(`${BAN_URL_RECHERCHE}?${params.toString()}`);
    if (res.ok) {
      const features = ((await res.json()) as { features?: FeatureBan[] } | null)?.features;
      // Règle d'acceptation PARTAGÉE (API, backup-manager) : score, même code
      // postal ou ville nommée, territoire attendu, homonymes → rien.
      const retenue = adresseDepuisFeatureBan(
        resultatBanRetenu(features, {
          texte,
          codePostal: analyse.codePostal,
          territoireAttendu: attendu,
          villeLieu: props.villeLieu ?? null,
        }),
      );
      if (retenue && estNombre(retenue.latitude) && estNombre(retenue.longitude)) {
        coords.value = { lat: retenue.latitude, lng: retenue.longitude };
        precisionPoint.value = retenue.precision;
        return;
      }
    }
  } catch {
    // intentional empty: error flagged below
  }
  mapError.value = true;
}

async function initMap() {
  if (!mapContainer.value || !coords.value) return;

  const L = await import("leaflet");

  // Fix Leaflet default marker icons (Vite issue connu) — Leaflet utilise
  // `_getIconUrl()` pour résoudre le chemin de l'icône via webpack assets.
  // Vite ne fournit pas cette fn, il faut donc supprimer la prop privée
  // pour forcer Leaflet à utiliser `mergeOptions(iconRetinaUrl, ...)` ci-dessous.
  // Les types officiels de Leaflet ne déclarent pas cette prop privée.
  delete (L.Icon.Default.prototype as { _getIconUrl?: unknown })._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  });

  mapInstance = L.map(mapContainer.value, {
    center: [coords.value.lat, coords.value.lng],
    // Règle partagée (§18.15, dette §17.14.5) : ville 12, pays 5, sinon 16 —
    // la même valeur que la mini-carte de l'app (fixtures `zoomsCarte`).
    zoom: zoomCarteDepuisPrecision(precisionPoint.value),
    zoomControl: false,
    // Mention posée à la main en bas à GAUCHE : le bouton « Itinéraire »
    // occupe le coin bas-droit.
    attributionControl: false,
    dragging: false,
    scrollWheelZoom: false,
    doubleClickZoom: false,
    touchZoom: false,
  });

  // Tuiles CARTO Voyager (plus moderne que OSM par défaut). Depuis le
  // 23/09/2026, CARTO exige une clé (`?key=`), sinon chaque tuile est une
  // image « API KEY REQUIRED » : clé gratuite (offre commerciale ≤ 1 M de
  // tuiles/mois), NUXT_PUBLIC_CARTO_KEY. Mention « © OpenStreetMap, © CARTO »
  // OBLIGATOIRE dans leurs conditions, même en gratuit.
  const cle = useRuntimeConfig().public.cartoKey;
  L.tileLayer(
    `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png${cle ? `?key=${encodeURIComponent(cle)}` : ""}`,
    {
      maxZoom: 19,
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>, © <a href="https://carto.com/attributions" target="_blank" rel="noopener">CARTO</a>',
    },
  ).addTo(mapInstance);
  L.control.attribution({ position: "bottomleft", prefix: false }).addTo(mapInstance);

  // Marqueur custom
  const customIcon = L.divIcon({
    html: `<div style="width:32px;height:32px;background:#212121;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;"><div style="width:8px;height:8px;background:white;border-radius:50%;transform:rotate(45deg);"></div></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    className: "",
  });

  L.marker([coords.value.lat, coords.value.lng], { icon: customIcon }).addTo(mapInstance);

  // Force le recalcul des dimensions après le rendu
  setTimeout(() => {
    mapInstance?.invalidateSize();
    mapReady.value = true;
  }, 100);
}

onMounted(async () => {
  try {
    if (!coords.value) await geocode();
    if (coords.value) {
      await nextTick();
      initMap();
    }
  } catch (err) {
    mapError.value = true;
    logger.error("[MiniMap] onMounted failed:", err);
  }
});

onBeforeUnmount(() => {
  if (mapInstance) {
    mapInstance.remove();
    mapInstance = null;
  }
});

// Itinéraire : règle partagée `lienItineraire` (coordonnées d'abord, sinon
// le texte ; format et encodage identiques web et mobile).
const googleMapsUrl = computed(
  () =>
    lienItineraire(
      { latitude: coords.value?.lat ?? null, longitude: coords.value?.lng ?? null, libelle: props.address },
      { plateforme: "google" },
    ) ?? undefined,
);
</script>

<template>
  <div class="relative" :style="{ height: height || '180px' }">
    <!-- Map container -->
    <div ref="mapContainer" class="w-full h-full z-0" />

    <!-- Loading overlay -->
    <Transition
      enter-active-class="transition-opacity duration-200"
      leave-active-class="transition-opacity duration-300"
      leave-to-class="opacity-0"
    >
      <div v-if="!mapReady" class="absolute inset-0 bg-gray-100 dark:bg-gray-800 flex items-center justify-center z-10">
        <div v-if="!mapError" class="flex items-center gap-2">
          <UIcon name="i-lucide-loader-2" class="size-5 text-gray-400 animate-spin" />
        </div>
        <UIcon v-else name="i-lucide-map-pin" class="size-10 text-gray-300 dark:text-gray-600" />
      </div>
    </Transition>

    <!-- Itinéraire button -->
    <a
      v-if="googleMapsUrl"
      :href="googleMapsUrl"
      target="_blank"
      class="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/95 dark:bg-[#141414]/95 backdrop-blur-sm text-xs font-semibold text-[#212121] dark:text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all"
    >
      <UIcon name="i-lucide-navigation" class="size-3.5" />
      {{ $t("association.champsFormulaire.itineraire") }}
    </a>
  </div>
</template>

<style scoped>
/* Mention CARTO / OpenStreetMap obligatoire, mais discrète. */
:deep(.leaflet-control-attribution) {
  font-size: 9px;
  line-height: 1.3;
  padding: 1px 5px;
  border-radius: 6px 6px 6px 0;
  background: rgb(255 255 255 / 0.7);
  color: #6b7280;
}
:deep(.leaflet-control-attribution a) {
  color: inherit;
}
</style>
